// biome-ignore lint/style/useFilenamingConvention: TanStack Router dynamic route parameter
import { createFileRoute } from "@tanstack/react-router";
import { posthog } from "posthog-js";
import { capabilitiesQueryOptions } from "@/app/capabilities/api/get-capabilities";
import { applyFirstWinGateRedirects } from "@/app/onboarding-first-win/server/apply-first-win-gate-redirects";
import { resolveFirstWinGate } from "@/app/onboarding-first-win/server/resolve-first-win-gate";
import { ensurePostHogIdentified } from "@/app/onboarding-first-win/server/wait-for-posthog-flags";
import { Layout } from "@/components/custom/layout";
import { type SessionData, sessionQueryOptions } from "@/lib/auth-client";
import { OrgProjectRouteError } from "@/lib/org-project-context/constants";
import { OrgProjectErrorFallback } from "@/lib/org-project-context/error-fallbacks";
import { resolveDashboardOrgProjectRoute } from "@/lib/org-project-context/hooks";

export const Route = createFileRoute(
  "/_authenticated/orgs/$organizationId/projects/$projectId"
)({
  beforeLoad: async ({ params, context, location }) => {
    const result = await resolveDashboardOrgProjectRoute(
      context.queryClient,
      params
    );

    if (result.outcome !== "valid") {
      throw new OrgProjectRouteError(result);
    }

    context.queryClient.prefetchQuery(
      capabilitiesQueryOptions({
        organizationId: result.organizationId,
        projectId: result.projectId,
      })
    );

    const session = context.queryClient.getQueryData<SessionData>(
      sessionQueryOptions.queryKey
    );
    const accountId =
      session?.user?.accountId ?? session?.user?.id ?? undefined;
    const didIdentify = ensurePostHogIdentified(posthog, accountId);

    const onboardingBasePath = `/orgs/${result.organizationId}/projects/${result.projectId}/onboarding`;
    const isOnOnboardingPath =
      location.pathname === onboardingBasePath ||
      location.pathname.startsWith(`${onboardingBasePath}/`);

    const gate = await resolveFirstWinGate({
      posthog,
      queryClient: context.queryClient,
      organizationId: result.organizationId,
      projectId: result.projectId,
      reloadAfterIdentify: didIdentify,
    });

    applyFirstWinGateRedirects(
      gate,
      isOnOnboardingPath,
      result.organizationId,
      result.projectId
    );

    return {
      organizationId: result.organizationId,
      projectId: result.projectId,
      organizations: result.organizations,
      projects: result.projects,
    };
  },
  component: Layout,
  errorComponent: OrgProjectErrorFallback,
});
